import { GENIE_INTERACTIVE_SELECTOR } from "./stage-container";

const SKIP_TAGS = new Set([
  "script",
  "style",
  "noscript",
  "svg",
  "path",
  "circle",
  "rect",
  "line",
  "polyline",
  "polygon",
  "ellipse",
  "defs",
  "mask",
  "clippath",
  "g",
  "symbol",
  "use",
  "filter",
  "feblend",
  "lineargradient",
  "stop",
  "br",
  "hr",
  "img",
  "video",
  "audio",
  "canvas",
  "iframe",
]);

const STRUCTURAL_TAGS = new Set([
  "main",
  "nav",
  "header",
  "footer",
  "section",
  "article",
  "aside",
  "form",
  "fieldset",
  "dialog",
]);

const SEMANTIC_TAGS = new Set([
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "label",
  "li",
  "th",
  "td",
  "caption",
  "figcaption",
  "table",
  "thead",
  "tbody",
  "tr",
  "ul",
  "ol",
]);

const INTERACTIVE_TAGS = new Set([
  "button",
  "a",
  "input",
  "select",
  "textarea",
  "details",
  "summary",
]);

const VALUE_TAGS = new Set(["input", "textarea", "select"]);

const INTERACTIVE_ROLES = new Set([
  "button",
  "tab",
  "menuitem",
  "checkbox",
  "switch",
  "combobox",
  "option",
  "link",
  "tabpanel",
  "menu",
  "listbox",
]);

// A menu trigger names its items here: a closed menu's items are not on the
// page, and Genie names them without opening it.
const GENIE_MENU_ATTR = "data-genie-menu";
// Built at runtime: the build target predates the regex "u" flag literal.
const LETTER_OR_DIGIT = new RegExp("[\\p{L}\\p{N}]", "u");

export function genieMenuProps(items: string[]): Record<string, string> {
  return items.length ? { [GENIE_MENU_ATTR]: JSON.stringify(items) } : {};
}

interface Counter {
  n: number;
}

export function isVisible(el: HTMLElement): boolean {
  const style = getComputedStyle(el);
  // display: contents has no box of its own; its children decide.
  if (
    style.display !== "contents" &&
    el.offsetWidth === 0 &&
    el.offsetHeight === 0
  )
    return false;
  // An open Radix menu or dialog hides the rest of the page from screen
  // readers (aria-hidden, marked data-aria-hidden); it is still on screen, and
  // Genie needs it to say where else to look.
  if (
    el.getAttribute("aria-hidden") === "true" &&
    !el.hasAttribute("data-aria-hidden")
  )
    return false;
  if (style.display === "none" || style.visibility === "hidden") return false;
  if (style.opacity === "0") return false;
  return true;
}

function getLabel(el: HTMLElement): string {
  const ariaLabel = el.getAttribute("aria-label");
  if (ariaLabel) return ariaLabel.trim();

  const labelledBy = el.getAttribute("aria-labelledby");
  if (labelledBy) {
    const labelEl = document.getElementById(labelledBy);
    if (labelEl?.textContent) return labelEl.textContent.trim();
  }

  const placeholder = el.getAttribute("placeholder");
  if (placeholder) return placeholder.trim();

  const title = el.getAttribute("title");
  if (title) return title.trim();

  return "";
}

function getDirectText(el: HTMLElement): string {
  let text = "";
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = node.textContent?.trim();
      if (t) text += (text ? " " : "") + t;
    }
  }
  return text.length > 80 ? text.slice(0, 80) + "..." : text;
}

function isInteractive(el: HTMLElement): boolean {
  const tag = el.tagName.toLowerCase();
  if (INTERACTIVE_TAGS.has(tag)) return true;
  const role = el.getAttribute("role");
  if (role && INTERACTIVE_ROLES.has(role)) return true;
  if (el.hasAttribute("onclick") || el.hasAttribute("tabindex")) return true;
  return false;
}

function isStructural(el: HTMLElement): boolean {
  const tag = el.tagName.toLowerCase();
  if (STRUCTURAL_TAGS.has(tag)) return true;
  const role = el.getAttribute("role");
  if (
    role === "navigation" ||
    role === "main" ||
    role === "banner" ||
    role === "complementary"
  )
    return true;
  return false;
}

function isSemantic(el: HTMLElement): boolean {
  return SEMANTIC_TAGS.has(el.tagName.toLowerCase());
}

function serializeTag(el: HTMLElement): string {
  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute("role");
  if (role === "tab") return "tab";
  if (role === "menuitem") return "menuitem";
  if (role === "navigation" || tag === "nav") return "nav";
  if (tag === "thead") return "thead";
  if (tag === "tbody") return "tbody";
  if (tag === "table") return "table";
  return tag;
}

function serializeAttributes(el: HTMLElement, genieId: string | null): string {
  const parts: string[] = [];
  if (genieId) parts.push(genieId);

  const tag = el.tagName.toLowerCase();

  if (tag === "a") {
    const href = (el as HTMLAnchorElement).pathname;
    if (href && href !== "/") parts.push(`href="${href}"`);
  }

  if (tag === "input") {
    const type = (el as HTMLInputElement).type;
    if (type && type !== "text") parts.push(`type="${type}"`);
  }

  // Never serialize the contents of any value-bearing field - they reach a
  // third-party LLM and any of them can hold secrets. Accessibility-tree
  // agents expose role/state, not values; we mirror that with a presence-only
  // flag. Playwright snapshots: https://playwright.dev/agent-cli/snapshots
  if (tag === "input" || tag === "textarea" || tag === "select") {
    const value = (
      el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    ).value;
    if (value) parts.push("filled");
  }

  if (
    el.getAttribute("aria-selected") === "true" ||
    el.getAttribute("data-state") === "active"
  )
    parts.push("active");
  if (el.getAttribute("aria-expanded") === "true") parts.push("expanded");
  const menu = el.getAttribute(GENIE_MENU_ATTR);
  if (menu) {
    const items = (JSON.parse(menu) as string[]).map((item) =>
      item.replaceAll('"', "'")
    );
    parts.push(`menu="${items.join(" | ")}"`);
  }
  if (
    el.hasAttribute("disabled") ||
    el.getAttribute("aria-disabled") === "true"
  )
    parts.push("disabled");

  return parts.length > 0 ? " " + parts.join(" ") : "";
}

function renderElement(
  indent: string,
  stag: string,
  attrs: string,
  landmarkAttr: string,
  directText: string,
  children: string
): string {
  let result = `${indent}<${stag}${attrs}${landmarkAttr}>`;
  if (directText && children) {
    result += directText + "\n" + children + indent;
  } else if (directText) {
    result += directText;
  } else {
    result += "\n" + children + indent;
  }
  result += `</${stag}>\n`;
  return result;
}

function serializeNode(
  el: HTMLElement,
  depth: number,
  maxDepth: number,
  counter: Counter
): string {
  if (depth > maxDepth) return "";

  const tag = el.tagName.toLowerCase();
  if (SKIP_TAGS.has(tag)) return "";
  if (!isVisible(el)) return "";

  // Skip genie's own UI - chat panel and spotlight overlay (and any wrappers)
  if (el.closest(GENIE_INTERACTIVE_SELECTOR)) return "";

  // Skip Pylon chat widget
  if (el.id === "pylon-chat-widget" || el.closest("#pylon-chat-widget"))
    return "";
  const ariaLabel = el.getAttribute("aria-label");
  if (
    ariaLabel === "Pylon Chat Widget" ||
    ariaLabel === "Customer support window" ||
    ariaLabel === "Open customer support"
  )
    return "";

  const indent = "  ".repeat(depth);

  if (isInteractive(el)) {
    const id = `ge-${counter.n++}`;
    el.setAttribute("data-genie-id", id);

    const landmarkParent = el.closest("[data-genie-landmark]");
    const landmarkId = landmarkParent?.getAttribute("data-genie-landmark");
    const landmarkAttr = landmarkId ? ` landmark="${landmarkId}"` : "";

    const isLeaf =
      INTERACTIVE_TAGS.has(tag) && tag !== "details" && tag !== "summary";

    if (isLeaf) {
      const label = VALUE_TAGS.has(tag)
        ? getLabel(el)
        : getLabel(el) ||
          getDirectText(el) ||
          el.textContent?.trim().slice(0, 80) ||
          "";
      if (!label) return "";
      const stag = serializeTag(el);
      return `${indent}<${stag}${serializeAttributes(el, id)}${landmarkAttr}>${label}</${stag}>\n`;
    }

    const children = serializeChildren(el, depth + 1, maxDepth, counter);
    const directText = getDirectText(el);
    if (!children && !directText) return "";
    return renderElement(
      indent,
      serializeTag(el),
      serializeAttributes(el, id),
      landmarkAttr,
      directText,
      children
    );
  }

  if (isStructural(el) || isSemantic(el)) {
    return serializeContainer(el, depth, maxDepth, counter);
  }

  // Non-semantic div/span: render only if it carries meaningful content
  const children = serializeChildren(el, depth, maxDepth, counter);
  const directText = getDirectText(el);

  if (directText && !children) {
    // A column header or a cell can be short ("sku", "ID", "7"): keep any
    // text with a letter or digit, drop separators and icon glyphs.
    if (LETTER_OR_DIGIT.test(directText))
      return `${indent}<p>${directText}</p>\n`;
    return "";
  }

  if (children) {
    if (directText) {
      return `${indent}<section>${directText}\n${children}${indent}</section>\n`;
    }
    return children;
  }

  return "";
}

function serializeContainer(
  el: HTMLElement,
  depth: number,
  maxDepth: number,
  counter: Counter
): string {
  const children = serializeChildren(el, depth + 1, maxDepth, counter);
  const directText = getDirectText(el);
  if (!children && !directText) return "";

  return renderElement(
    "  ".repeat(depth),
    serializeTag(el),
    serializeAttributes(el, null),
    "",
    directText,
    children
  );
}

function serializeChildren(
  el: HTMLElement,
  depth: number,
  maxDepth: number,
  counter: Counter
): string {
  let result = "";
  for (const child of el.children) {
    if (child instanceof HTMLElement) {
      result += serializeNode(child, depth, maxDepth, counter);
    }
  }
  return result;
}

export interface DomSnapshot {
  html: string;
  interactiveCount: number;
}

export function takeSnapshot(): DomSnapshot {
  for (const stale of document.querySelectorAll("[data-genie-id]")) {
    stale.removeAttribute("data-genie-id");
  }
  const counter: Counter = { n: 0 };
  const html = serializeNode(document.body, 0, 15, counter);
  return {
    html: html.trim(),
    interactiveCount: counter.n,
  };
}

// The name the snapshot shows for an interactive element.
export function elementLabel(el: HTMLElement): string {
  return getLabel(el) || el.textContent?.trim().slice(0, 80) || "";
}

function normalize(text: string): string {
  return text.replace(/\s+/g, " ").trim().toLowerCase();
}

// Whether ``label`` is a name the snapshot can show for ``el``: its accessible
// label, its own text or its whole text, cut as the snapshot cuts it. A
// fragment ("table" for "Delete table") is not.
export function hasLabel(el: HTMLElement, label: string): boolean {
  const wanted = normalize(label);
  const text = el.textContent?.trim() ?? "";
  return (
    wanted !== "" &&
    [getLabel(el), getDirectText(el), text, text.slice(0, 80)].some(
      (name) => normalize(name) === wanted
    )
  );
}

export function getSnapshotElement(genieId: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-genie-id="${genieId}"]`);
}
